import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  LogOut, Calendar, DollarSign, Bus, Bell, 
  CheckCircle2, AlertCircle, X, QrCode, BookOpen, Upload, FileText
} from 'lucide-react';
import { schoolInfo } from '../utils/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Animation variants for cards
const cardAnimation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 }
};

const ParentDashboardPage = () => {
  const navigate = useNavigate();
  const hasFetched = useRef(false); // Prevent double execution in React 18 Strict Mode
  
  const [studentData, setStudentData] = useState(null);
  const [notices, setNotices] = useState([]); // Static for now
  const [homework, setHomework] = useState([]); // Static for now
  const [submissions, setSubmissions] = useState([]); // Static for now
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [selectedHomework, setSelectedHomework] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submissionForm, setSubmissionForm] = useState({
    submission_file: '',
    comment: ''
  });

  useEffect(() => {
    // SINGLE EXECUTION ONLY
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchOnce = async () => {
      try {
        // Get mobile from localStorage
        const parentData = JSON.parse(localStorage.getItem('parentData') || '{}');
        const mobile = parentData.mobile || parentData.mobile_number;
        
        if (!mobile) {
          navigate('/login');
          return;
        }

        // STEP 1: FETCH STUDENT DATA
        console.log('📡 Fetching student data for:', mobile);
        const response = await axios.get(`${BACKEND_URL}/api/students/${mobile}/dashboard`);
        console.log('✅ Student data:', response.data);
        
        // SET STUDENT DATA
        setStudentData(response.data);
        console.log('✅ studentData SET:', response.data.student_name);
        console.log('  - Attendance:', response.data.attendance);
        console.log('  - Fees:', response.data.fees);
        console.log('  - Bus:', response.data.bus_tracking);
        
        // STEP 2: FETCH HOMEWORK for this class (use full class_name with "Class " prefix)
        const className = response.data.class_name; // Keep as-is: "Class 5-A"
        console.log('📚 Fetching homework for class:', className);
        
        try {
          const hwResponse = await axios.get(`${BACKEND_URL}/api/homework/class/${className}`);
          console.log('✅ Homework data:', hwResponse.data);
          setHomework(hwResponse.data || []);
          console.log('✅ homework SET:', hwResponse.data?.length || 0, 'items');
        } catch (hwErr) {
          console.log('⚠️ Homework fetch failed:', hwErr.message);
          setHomework([]);
        }
        
        // STEP 3: FETCH NOTICES
        console.log('📢 Fetching notices...');
        try {
          const notResponse = await axios.get(`${BACKEND_URL}/api/notices`);
          console.log('✅ Notices data:', notResponse.data);
          setNotices(notResponse.data?.slice(0, 3) || []);
          console.log('✅ notices SET:', notResponse.data?.length || 0, 'items');
        } catch (notErr) {
          console.log('⚠️ Notices fetch failed:', notErr.message);
          setNotices([]);
        }
        
        // STEP 4: FETCH SUBMISSIONS
        console.log('📤 Fetching submissions...');
        try {
          const subResponse = await axios.get(`${BACKEND_URL}/api/homework/student/${response.data.student_name}/submissions`);
          console.log('✅ Submissions data:', subResponse.data);
          setSubmissions(subResponse.data || []);
          console.log('✅ submissions SET:', subResponse.data?.length || 0, 'items');
        } catch (subErr) {
          console.log('⚠️ Submissions fetch failed:', subErr.message);
          setSubmissions([]);
        }
        
        // DONE - SET LOADING FALSE
        console.log('💾 Setting loading = false');
        setLoading(false);
        console.log('🎉 FETCH COMPLETE');
        
      } catch (error) {
        console.error('❌ Fatal error:', error);
        console.error('   Message:', error.message);
        console.error('   Response:', error.response?.data);
        setLoading(false);
      }
    };

    fetchOnce();
  }, []); // EMPTY - RUN ONCE

  // LOG CURRENT STATE ON EVERY RENDER
  console.log('🎨 RENDER - studentData:', studentData ? studentData.student_name : 'NULL');
  console.log('🎨 RENDER - homework:', homework?.length || 0);
  console.log('🎨 RENDER - loading:', loading);

  // CRITICAL FIX: Define safe defaults BEFORE any return statements
  // These must be available even during loading state
  const student_name = studentData?.student_name || 'Student';
  const class_name = studentData?.class_name || 'Class';
  const parent_name = studentData?.parent_name || 'Parent';
  const attendance = studentData?.attendance || { total_days: 0, present_days: 0, percentage: 0 };
  const fees = studentData?.fees || { total: 0, paid: 0, due: 0, status: 'Unknown' };
  const bus_tracking = studentData?.bus_tracking || { bus_number: '', route: '', status: 'Not Assigned', driver_name: '', driver_mobile: '' };

  const handleLogout = () => {
    // Clear localStorage
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('isParentAuthenticated');
    localStorage.removeItem('parentData');
    
    // Clear sessionStorage (mobile, OTP, etc.)
    sessionStorage.clear();
    
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handlePayNow = () => {
    setShowPaymentModal(true);
  };

  const handlePaymentComplete = () => {
    setShowPaymentModal(false);
    
    // Open WhatsApp with pre-filled message
    const message = `I have paid the school fees. Please verify.

Student Name: ${studentData.student_name}
Class: ${studentData.class_name}
Amount Paid: ₹${fees.due.toLocaleString()}

*Please find the payment screenshot attached.*`;
    
    const encodedMessage = encodeURIComponent(message);
    const phoneNumber = schoolInfo.phone.replace(/[^0-9]/g, '');
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
    
    toast.success('Opening WhatsApp to share payment confirmation', {
      duration: 4000,
      description: 'Please attach your payment screenshot'
    });
  };

  const handleShareOnWhatsApp = () => {
    const message = `Payment completed for ${studentData.student_name} (${studentData.class_name})\nAmount: ₹${fees.due.toLocaleString()}\nAMTEE PUBLIC SCHOOL`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${schoolInfo.phone.replace(/[^0-9]/g, '')}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleSubmitAssignment = (hw) => {
    setSelectedHomework(hw);
    setShowSubmissionModal(true);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSubmissionForm({
          ...submissionForm,
          submission_file: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitHomework = async (e) => {
    e.preventDefault();
    
    // Validate file
    if (!submissionForm.submission_file) {
      toast.error('Please upload a file');
      return;
    }

    // Validate student data exists
    if (!studentData || !studentData.student_name || !studentData.class_name) {
      toast.error('Student information not found', {
        description: 'Please refresh the page and try again'
      });
      return;
    }

    // Set loading state
    setSubmitting(true);

    try {
      console.log('Submitting homework for:', studentData.student_name);
      
      const response = await axios.post(`${BACKEND_URL}/api/homework/submit`, {
        homework_id: selectedHomework.id,
        student_name: studentData.student_name,
        student_class: studentData.class_name,
        submission_file: submissionForm.submission_file,
        comment: submissionForm.comment
      });

      console.log('Homework submitted successfully:', response.data);

      toast.success('Assignment submitted successfully!', {
        description: 'Your homework has been submitted for review'
      });
      
      // Close modal and reset form
      setShowSubmissionModal(false);
      setSubmissionForm({ submission_file: '', comment: '' });

      // Refresh submissions without causing logout
      try {
        const submissionsResponse = await axios.get(`${BACKEND_URL}/api/homework/student/${studentData.student_name}/submissions`);
        setSubmissions(submissionsResponse.data);
      } catch (refreshErr) {
        console.warn('Failed to refresh submissions:', refreshErr);
        // Don't show error - submission was successful
      }
      
    } catch (error) {
      console.error('Homework submission error:', error);
      
      // Show specific error message without logging out
      const errorMessage = error.response?.data?.detail || 'Failed to submit assignment';
      toast.error('Submission failed', {
        description: errorMessage + '. Please try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to get submission for a homework
  const getSubmissionForHomework = (homeworkId) => {
    return submissions.find(sub => sub.homework_id === homeworkId);
  };

  // Show loading spinner ONLY if loading is true AND we have no data yet
  if (loading && !studentData) {
    console.log('🔄 Showing loading spinner');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-700 font-medium">Loading Dashboard...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait while we fetch your data</p>
        </div>
      </div>
    );
  }

  // Show error ONLY if not loading AND still no data AND we actually tried to fetch
  if (!loading && !studentData && hasFetched.current) {
    console.log('❌ Showing error screen');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 px-4">
        <Card className="max-w-md w-full shadow-2xl">
          <CardContent className="py-12 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-12 w-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">No Student Data Found</h2>
            <p className="text-gray-600 mb-4">We couldn't find your student information.</p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700 mb-2 font-medium">This might happen if:</p>
              <ul className="text-sm text-gray-600 space-y-1 text-left list-disc list-inside">
                <li>Your registration is pending</li>
                <li>The mobile number is not registered</li>
                <li>There was a connection error</li>
              </ul>
            </div>
            <div className="flex flex-col gap-3">
              <Button onClick={() => window.location.reload()} className="w-full bg-blue-600 hover:bg-blue-700">
                Refresh Page
              </Button>
              <Button onClick={handleLogout} variant="outline" className="w-full">
                Back to Login
              </Button>
            </div>
            <div className="mt-6 pt-6 border-t">
              <p className="text-sm text-gray-600 mb-2">Need assistance?</p>
              <p className="text-sm font-semibold text-blue-600">📞 {schoolInfo.phone}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  console.log('🎯 RENDERING MAIN DASHBOARD');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Compact Header with smooth shadow */}
      <header className="bg-white shadow-md border-b sticky top-0 z-50 backdrop-blur-sm bg-opacity-95 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <img 
                src={schoolInfo.logo} 
                alt="School Logo" 
                className="h-10 w-10 sm:h-12 sm:w-12 object-contain transform hover:scale-110 transition-transform duration-300"
              />
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-bold text-gray-900 truncate">{student_name}</h1>
                <p className="text-xs sm:text-sm text-gray-600">{class_name}</p>
              </div>
            </div>
            <Button 
              size="sm"
              variant="ghost" 
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 transition-all duration-200 rounded-lg px-3 py-2"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline font-medium">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8 sm:px-6 lg:px-8">
        {/* Welcome Message */}
        <div className="mb-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
            Welcome, {parent_name}! 👋
          </h2>
          <p className="text-sm sm:text-base text-gray-600">
            Here's {student_name}'s latest updates
          </p>
        </div>

        {/* Dashboard Cards - 2x2 Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-7 lg:gap-8">
          
          {/* Attendance Card */}
          <Card className="shadow-xl hover:shadow-2xl transition-all duration-500 border-0 border-l-4 border-l-green-500 rounded-2xl overflow-hidden group hover:scale-[1.02]">
            <CardHeader className="pb-4 bg-gradient-to-r from-green-50 to-white group-hover:from-green-100 transition-colors duration-300">
              <CardTitle className="flex items-center justify-between text-lg sm:text-xl">
                <span className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                  <span className="font-bold text-gray-900">Attendance</span>
                </span>
                <span className="text-2xl sm:text-3xl font-bold text-green-600 animate-pulse-slow">
                  {attendance.percentage.toFixed(1)}%
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <Progress value={attendance.percentage} className="mb-4 h-3 bg-gray-200" />
              <div className="flex justify-between text-center">
                <div className="flex-1">
                  <p className="text-green-600 font-bold text-xl sm:text-2xl">{attendance.present_days}</p>
                  <p className="text-gray-500 text-xs sm:text-sm mt-1">Present</p>
                </div>
                <div className="flex-1 border-x border-gray-200">
                  <p className="text-gray-900 font-bold text-xl sm:text-2xl">{attendance.total_days}</p>
                  <p className="text-gray-500 text-xs sm:text-sm mt-1">Total Days</p>
                </div>
                <div className="flex-1">
                  <p className="text-red-600 font-bold text-xl sm:text-2xl">
                    {attendance.total_days - attendance.present_days}
                  </p>
                  <p className="text-gray-500 text-xs sm:text-sm mt-1">Absent</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fees Card */}
          <Card className="shadow-xl hover:shadow-2xl transition-all duration-500 border-0 border-l-4 border-l-blue-500 rounded-2xl overflow-hidden group hover:scale-[1.02]">
            <CardHeader className="pb-4 bg-gradient-to-r from-blue-50 to-white group-hover:from-blue-100 transition-colors duration-300">
              <CardTitle className="flex items-center justify-between text-lg sm:text-xl">
                <span className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                  <span className="font-bold text-gray-900">Fee Details</span>
                </span>
                <span className={`px-3 py-1.5 rounded-full text-xs font-bold shadow-sm transition-all duration-300 ${
                  fees.status === 'Paid' 
                    ? 'bg-green-100 text-green-800 border border-green-200 hover:bg-green-200' 
                    : 'bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200'
                }`}>
                  {fees.status}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              {/* Fee Breakdown with smooth transitions */}
              <div className="space-y-3 mb-4">
                {/* Total Fees */}
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200">
                  <span className="text-sm font-medium text-gray-700">Total Fees</span>
                  <span className="text-base sm:text-lg font-bold text-gray-900">₹{fees.total.toLocaleString()}</span>
                </div>
                
                {/* Paid Amount */}
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl hover:bg-green-100 transition-colors duration-200">
                  <span className="text-sm font-medium text-green-700">Paid Amount</span>
                  <span className="text-base sm:text-lg font-bold text-green-700">₹{fees.paid.toLocaleString()}</span>
                </div>
                
                {/* Due Amount - Highlighted */}
                <div className={`flex justify-between items-center p-4 rounded-xl border-2 transition-all duration-300 ${
                  fees.due > 0 
                    ? 'bg-red-50 border-red-200 hover:bg-red-100 hover:border-red-300' 
                    : 'bg-green-50 border-green-200 hover:bg-green-100 hover:border-green-300'
                }`}>
                  <span className={`text-sm font-bold ${
                    fees.due > 0 ? 'text-red-700' : 'text-green-700'
                  }`}>
                    Due Amount
                  </span>
                  <span className={`text-xl sm:text-2xl font-bold ${
                    fees.due > 0 ? 'text-red-600' : 'text-green-600'
                  }`}>
                    ₹{fees.due.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Pay Now Button or Paid Status */}
              {fees.due > 0 ? (
                <Button 
                  onClick={handlePayNow}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] rounded-xl"
                >
                  <DollarSign className="mr-2 h-4 w-4" />
                  Pay Now
                </Button>
              ) : (
                <div className="flex items-center justify-center gap-2 p-4 bg-green-50 border-2 border-green-200 rounded-xl hover:bg-green-100 transition-all duration-200">
                  <CheckCircle2 className="h-5 w-5 text-green-600 animate-pulse" />
                  <span className="text-sm font-semibold text-green-700">All Fees Paid!</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bus Tracking Card */}
          <Card className="shadow-xl hover:shadow-2xl transition-all duration-500 border-0 border-l-4 border-l-purple-500 rounded-2xl overflow-hidden group hover:scale-[1.02]">
            <CardHeader className="pb-4 bg-gradient-to-r from-purple-50 to-white group-hover:from-purple-100 transition-colors duration-300">
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <Bus className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
                <span className="font-bold text-gray-900">Bus Status</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {bus_tracking.status === 'Not Assigned' ? (
                <div className="text-center py-8">
                  <Bus className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-3 animate-pulse" />
                  <p className="text-sm text-gray-500 font-medium">Bus not assigned</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                    <span className="text-sm text-gray-600">Status:</span>
                    <span className={`px-3 py-1.5 rounded-full text-xs font-bold shadow-sm transition-all duration-300 ${
                      bus_tracking.status === 'Arrived' 
                        ? 'bg-green-100 text-green-800 border border-green-200 hover:bg-green-200' 
                        : 'bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200'
                    }`}>
                      {bus_tracking.status}
                    </span>
                  </div>
                  <div className="space-y-3 p-3 bg-gray-50 rounded-xl">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Bus Number</p>
                      <p className="font-bold text-gray-900">{bus_tracking.bus_number}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Route</p>
                      <p className="font-bold text-gray-900">{bus_tracking.route}</p>
                    </div>
                  </div>
                  {bus_tracking.driver_name && (
                    <div className="p-3 bg-purple-50 rounded-xl border border-purple-100">
                      <p className="text-xs text-purple-600 font-semibold mb-1">Driver Details</p>
                      <p className="text-sm font-medium text-gray-900">{bus_tracking.driver_name}</p>
                      <p className="text-xs text-gray-500 mt-1">{bus_tracking.driver_mobile}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notices Card */}
          <Card className="shadow-xl hover:shadow-2xl transition-all duration-500 border-0 border-l-4 border-l-orange-500 rounded-2xl overflow-hidden group hover:scale-[1.02]">
            <CardHeader className="pb-4 bg-gradient-to-r from-orange-50 to-white group-hover:from-orange-100 transition-colors duration-300">
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <Bell className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600" />
                <span className="font-bold text-gray-900">Latest Notices</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {notices.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Bell className="h-8 w-8 text-orange-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Notices Available</h3>
                  <p className="text-sm text-gray-500">
                    There are no new notices at the moment.
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Check back later for updates from school.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notices.map((notice, index) => (
                    <div 
                      key={notice.id} 
                      className="p-3 border-b last:border-0 hover:bg-orange-50 rounded-lg transition-all duration-200 animate-fade-in"
                      style={{ animationDelay: `${0.5 + index * 0.1}s` }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-sm text-gray-900 line-clamp-2 flex-1">
                          {notice.title}
                        </p>
                        {notice.tag === 'Important' && (
                          <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full font-bold flex-shrink-0 border border-red-200 animate-pulse-slow">
                            Important
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">{notice.date}</p>
                    </div>
                  ))}
                  <Button 
                    variant="outline"
                    size="sm"
                    className="w-full mt-3 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700 transition-all duration-200 rounded-xl font-medium py-2.5"
                    onClick={() => navigate('/notices')}
                  >
                    View All Notices
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Homework Section */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-green-600" />
            Homework & Assignments
          </h2>
          
          {homework.length === 0 ? (
            <Card className="shadow-xl border-2 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-white rounded-2xl">
              <CardContent className="py-12 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No Homework Yet</h3>
                <p className="text-gray-600 mb-4">
                  Your teacher hasn't assigned any homework for class {class_name} yet.
                </p>
                <p className="text-sm text-gray-500">
                  Check back later for new assignments.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-7">
              {homework.map((hw, index) => {
                const submission = getSubmissionForHomework(hw.id);
                return (
                <Card 
                  key={hw.id} 
                  className="shadow-xl hover:shadow-2xl transition-all duration-300 border-l-4 border-l-green-500 rounded-2xl"
                >
                  <CardHeader className="pb-3 bg-gradient-to-r from-green-50 to-white">
                    <CardTitle className="flex items-center justify-between text-lg">
                      <span className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-green-600" />
                        {hw.title}
                      </span>
                      <span className="text-sm text-gray-500">{hw.date}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <p className="text-gray-700 mb-4">{hw.description}</p>
                    {hw.image && (
                      <div className="mb-4">
                        <img 
                          src={hw.image} 
                          alt="Homework" 
                          className="w-full max-h-64 object-cover rounded-lg border-2 border-gray-200"
                        />
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4 p-3 bg-blue-50 rounded-lg">
                      <span>Teacher: {hw.teacher_name}</span>
                      <span>Class: {hw.class_name}</span>
                    </div>

                    {/* Submission Status */}
                    {submission ? (
                      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-gray-900">Your Submission</h4>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            submission.status === 'Graded' ? 'bg-green-100 text-green-700' :
                            submission.status === 'Reviewed' ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {submission.status}
                          </span>
                        </div>
                        
                        {submission.grade && (
                          <div className="bg-white p-3 rounded-lg mb-2 border border-purple-200">
                            <p className="text-xs text-gray-500 mb-1">Grade</p>
                            <p className="text-lg font-bold text-purple-600">{submission.grade}</p>
                          </div>
                        )}

                        {submission.teacher_feedback && (
                          <div className="bg-white p-3 rounded-lg border border-blue-200">
                            <p className="text-xs text-gray-500 mb-1">Teacher's Feedback</p>
                            <p className="text-sm text-gray-700">{submission.teacher_feedback}</p>
                          </div>
                        )}

                        <p className="text-xs text-gray-500 mt-2">
                          Submitted on {new Date(submission.submitted_at).toLocaleDateString()}
                        </p>
                      </div>
                    ) : (
                      <Button
                        onClick={() => handleSubmitAssignment(hw)}
                        className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Submit Assignment
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )})}
            </div>
          )}
        </div>

        {/* Quick Info Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            {schoolInfo.name} • {schoolInfo.phone}
          </p>
        </div>
      </div>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md mx-auto p-0 overflow-hidden rounded-2xl">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white p-6 relative">
            <button
              onClick={() => setShowPaymentModal(false)}
              className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3 mb-2">
              <QrCode className="h-8 w-8" />
              <div>
                <DialogTitle className="text-xl font-bold text-white">Pay Fees via UPI</DialogTitle>
                <DialogDescription className="text-blue-100 text-sm">
                  Scan QR code to complete payment
                </DialogDescription>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Amount Display */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-4 text-center">
              <p className="text-sm text-gray-600 mb-1">Amount to Pay</p>
              <p className="text-3xl font-bold text-red-600">₹{fees.due.toLocaleString()}</p>
            </div>

            {/* QR Code */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-2xl border-2 border-orange-200">
              <div className="text-center mb-4">
                <h3 className="text-lg font-bold text-gray-900 mb-1">Scan & Pay using any UPI app</h3>
                <p className="text-sm text-gray-600">Google Pay • PhonePe • Paytm • BHIM</p>
              </div>
              
              <div className="bg-white p-4 rounded-xl shadow-lg mx-auto max-w-sm">
                <img 
                  src="/qr-payment.jpg" 
                  alt="UPI QR Code" 
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </div>

            {/* Payment Details */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">School Name:</span>
                <span className="font-semibold text-gray-900">AMTEE PUBLIC SCHOOL</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">UPI ID:</span>
                <span className="font-mono font-bold text-blue-600">9559970204@rapl</span>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-900 mb-2">Payment Instructions</p>
                  <ol className="text-xs text-amber-800 space-y-1.5 list-decimal list-inside">
                    <li>Scan the QR code using any UPI app</li>
                    <li>Verify the amount (₹{fees.due.toLocaleString()})</li>
                    <li>Complete the payment transaction</li>
                    <li>Take a screenshot of payment confirmation</li>
                    <li>Click "I Have Paid" button below</li>
                    <li>WhatsApp will open - attach your screenshot</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handlePaymentComplete}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-3.5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
              >
                <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                I Have Paid - Share Screenshot
              </Button>

              <div className="text-center">
                <p className="text-xs text-gray-500">
                  Clicking above will open WhatsApp to share payment proof
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assignment Submission Modal */}
      <Dialog open={showSubmissionModal} onOpenChange={setShowSubmissionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Submit Assignment</DialogTitle>
            <DialogDescription>
              {selectedHomework?.title}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitHomework} className="space-y-4">
            <div>
              <Label htmlFor="submission_file">Upload File/Image *</Label>
              <Input
                id="submission_file"
                type="file"
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileUpload}
                className="cursor-pointer"
                required
              />
              {submissionForm.submission_file && (
                <div className="mt-2 relative">
                  <img 
                    src={submissionForm.submission_file} 
                    alt="Preview" 
                    className="w-full h-32 object-cover rounded-lg border-2 border-green-200"
                  />
                  <button
                    type="button"
                    onClick={() => setSubmissionForm({...submissionForm, submission_file: ''})}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="comment">Comment (Optional)</Label>
              <Textarea
                id="comment"
                value={submissionForm.comment}
                onChange={(e) => setSubmissionForm({...submissionForm, comment: e.target.value})}
                placeholder="Add any notes or comments..."
                rows={3}
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowSubmissionModal(false)}
                className="flex-1"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Submit
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParentDashboardPage;
